import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { Search, Plus, User, Calendar, Clock, Heart, Baby, Users2 } from 'lucide-react';
import { formatName, getInitials } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { MedicalDiagnosisSelect, BvipDiagnosisSelect, DiagnosisPills } from '@/components/DiagnosisSelect';
import SearchableSelect from '@/components/SearchableSelect';
import PhoneField from '@/components/PhoneField';
import FieldShell from '@/components/FieldShell';
import PasswordStrength from '@/components/PasswordStrength';
import {
  validateFirstName, validateLastName, validateEmail, validatePhoneE164,
  validateOptionalPhoneE164, validateGender, validatePassword, validatePrimaryTherapist,
  validateChildDOB, validateAdultDOB, validateRelationshipDOB,
  validateCaregiverRelationship, countErrors,
} from '@/lib/validators';

const emptyPerson = { first_name: '', last_name: '', email: '', password: '', phone: '', date_of_birth: '', gender: '', blood_group: '', address: '', previous_medical_issues: '', primary_therapist_id: '' };
const emptyCaregiver = { name: '', first_name: '', last_name: '', relationship: '', phone: '', email: '', address: '' };

const inputCls = (err) =>
  `rounded-xl h-9 text-sm pr-7 ${err ? 'border-[#D3544E] focus-visible:ring-[#D3544E]/30' : 'border-[#E5F2F1]'}`;
const smInputCls = (err) =>
  `rounded-xl h-8 text-xs pr-7 ${err ? 'border-[#D3544E] focus-visible:ring-[#D3544E]/30' : 'border-[#E5F2F1]'}`;

function therapistRenderers() {
  return {
    getSearchText: (t) => `${t.first_name || ''} ${t.last_name || ''} ${t.email || ''}`,
    getLabel: (t) => {
      const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
      return `Dr. ${fn} ${t.last_name || ''}`.trim();
    },
    renderOption: (t) => {
      const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
      return <span className="text-xs font-medium text-[#1A3330]">{`Dr. ${fn} ${t.last_name || ''}`.trim()}</span>;
    },
    renderSelected: (t) => {
      const fn = (t.first_name || '').replace(/^Dr\.?\s*/i, '');
      return <span className="text-xs font-medium text-[#1A3330] truncate block">{`Dr. ${fn} ${t.last_name || ''}`.trim()}</span>;
    },
  };
}

export default function PatientsPage() {
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [filterBvip, setFilterBvip] = useState('');
  const [filterStatus, setFilterStatus] = useState('ACTIVE');
  const [showModal, setShowModal] = useState(false);
  const [therapists, setTherapists] = useState([]);
  const [clientType, setClientType] = useState('ADULT');
  const [form, setForm] = useState({ ...emptyPerson, primary_therapist_id: user?._id || '' });
  const [medDiag, setMedDiag] = useState([]);
  const [bvipDiag, setBvipDiag] = useState([]);
  const [caregiver, setCaregiver] = useState({ ...emptyCaregiver });
  const [partner1, setPartner1] = useState({ ...emptyPerson, primary_therapist_id: user?._id || '' });
  const [partner2, setPartner2] = useState({ ...emptyPerson, primary_therapist_id: user?._id || '' });
  const [relMedDiag, setRelMedDiag] = useState([]);
  const [relBvipDiag, setRelBvipDiag] = useState([]);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // touched/errors per scope: form / caregiver / p1 / p2
  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});
  const formRef = useRef(null);
  const trenderers = useMemo(therapistRenderers, []);

  const fetchClients = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (filterBvip) params.set('bvip', filterBvip);
      if (filterStatus) params.set('status', filterStatus);
      const { data } = await api.get(`/clients?${params}`);
      setClients(data);
    } catch {}
  };
  useEffect(() => { fetchClients(); }, [search, filterBvip, filterStatus]); // eslint-disable-line
  useEffect(() => { api.get('/therapists').then(r => setTherapists(r.data)).catch(() => {}); }, []);

  const resetForm = () => {
    setForm({ ...emptyPerson, primary_therapist_id: user?._id || '' });
    setMedDiag([]); setBvipDiag([]);
    setCaregiver({ ...emptyCaregiver });
    setPartner1({ ...emptyPerson, primary_therapist_id: user?._id || '' });
    setPartner2({ ...emptyPerson, primary_therapist_id: user?._id || '' });
    setRelMedDiag([]); setRelBvipDiag([]);
    setClientType('ADULT'); setError(''); setTouched({}); setErrors({});
  };

  // ---------------------------------------------------------------------------
  // Validation orchestration
  // ---------------------------------------------------------------------------
  const validateField = (scope, key, value, extras = {}) => {
    if (scope === 'form') {
      switch (key) {
        case 'first_name': return validateFirstName(value);
        case 'last_name': return validateLastName(value);
        case 'email': return validateEmail(value);
        case 'phone': return validatePhoneE164(value);
        case 'emergency_contact_phone': return validateOptionalPhoneE164(value);
        case 'date_of_birth':
          if (clientType === 'CHILD') return validateChildDOB(value);
          return validateAdultDOB(value);
        case 'gender': return validateGender(value);
        case 'password': return validatePassword(value);
        case 'primary_therapist_id': return validatePrimaryTherapist(value);
        default: return '';
      }
    }
    if (scope === 'caregiver') {
      switch (key) {
        case 'first_name': return validateFirstName(value);
        case 'last_name': return validateLastName(value);
        case 'phone': return validatePhoneE164(value);
        case 'email': return validateEmail(value);
        case 'relationship': return validateCaregiverRelationship(value);
        default: return '';
      }
    }
    if (scope === 'p1' || scope === 'p2') {
      switch (key) {
        case 'first_name': return validateFirstName(value);
        case 'last_name': return validateLastName(value);
        case 'email': {
          const base = validateEmail(value);
          if (base) return base;
          const other = scope === 'p1' ? partner2.email : partner1.email;
          if (value && other && value.trim().toLowerCase() === other.trim().toLowerCase())
            return 'Partner 1 and Partner 2 must have different email addresses.';
          return '';
        }
        case 'password': return validatePassword(value);
        case 'phone': {
          const base = validatePhoneE164(value);
          if (base) return base;
          const other = scope === 'p1' ? partner2.phone : partner1.phone;
          if (value && other && value === other)
            return 'Partner 1 and Partner 2 must have different phone numbers.';
          return '';
        }
        case 'date_of_birth': return validateRelationshipDOB(value);
        case 'gender': return validateGender(value);
        case 'primary_therapist_id': return validatePrimaryTherapist(value);
        default: return '';
      }
    }
    return '';
  };

  const errKey = (scope, key) => `${scope}.${key}`;

  const onBlur = (scope, key, value) => {
    setTouched(t => ({ ...t, [errKey(scope, key)]: true }));
    setErrors(prev => ({ ...prev, [errKey(scope, key)]: validateField(scope, key, value) }));
  };

  const onChange = (scope, key, value, setter, current) => {
    const next = { ...current, [key]: value };
    setter(next);
    // Live re-validate if already touched
    if (touched[errKey(scope, key)]) {
      setErrors(prev => ({ ...prev, [errKey(scope, key)]: validateField(scope, key, value) }));
    }
  };

  const runFullValidation = () => {
    const e = {};
    if (clientType !== 'RELATIONSHIP') {
      ['first_name','last_name','email','phone','date_of_birth','gender','password','primary_therapist_id']
        .forEach(k => { const msg = validateField('form', k, form[k]); if (msg) e[errKey('form', k)] = msg; });
      if (form.emergency_contact_phone) {
        const msg = validateField('form', 'emergency_contact_phone', form.emergency_contact_phone);
        if (msg) e[errKey('form', 'emergency_contact_phone')] = msg;
      }
      if (clientType === 'CHILD') {
        ['first_name','last_name','email','phone','relationship']
          .forEach(k => { const msg = validateField('caregiver', k, caregiver[k]); if (msg) e[errKey('caregiver', k)] = msg; });
      }
    } else {
      ['first_name','last_name','email','password','phone','date_of_birth','gender','primary_therapist_id']
        .forEach(k => { const msg = validateField('p1', k, partner1[k]); if (msg) e[errKey('p1', k)] = msg; });
      ['first_name','last_name','email','password','phone','date_of_birth','gender','primary_therapist_id']
        .forEach(k => { const msg = validateField('p2', k, partner2[k]); if (msg) e[errKey('p2', k)] = msg; });
    }
    return e;
  };

  const scrollToFirstError = (errMap) => {
    const firstKey = Object.keys(errMap)[0];
    if (!firstKey) return;
    const el = formRef.current?.querySelector(`[data-fieldkey="${firstKey}"]`);
    if (el && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setError('');
    const newErrors = runFullValidation();
    setErrors(newErrors);
    // Mark all as touched so errors render
    const t = {};
    Object.keys(newErrors).forEach(k => { t[k] = true; });
    setTouched(prev => ({ ...prev, ...t }));
    const n = countErrors(newErrors);
    if (n > 0) {
      setError(`Please fix ${n} error${n === 1 ? '' : 's'} before submitting.`);
      scrollToFirstError(newErrors);
      return;
    }
    setSubmitting(true);
    try {
      if (clientType === 'RELATIONSHIP') {
        await api.post('/clients/relationship', {
          medical_diagnosis: relMedDiag, bvip_diagnosis: relBvipDiag,
          partner1: { ...partner1 }, partner2: { ...partner2 },
        });
      } else {
        const cgPayload = clientType === 'CHILD'
          ? { ...caregiver, name: `${caregiver.first_name || ''} ${caregiver.last_name || ''}`.trim() }
          : null;
        await api.post('/clients', {
          ...form,
          medical_diagnosis: medDiag,
          bvip_diagnosis: bvipDiag,
          client_type: clientType,
          caregiver: cgPayload,
        });
      }
      setShowModal(false); resetForm(); fetchClients();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create patient');
    } finally {
      setSubmitting(false);
    }
  };

  const typeIcon = (ct) => ct === 'CHILD' ? <Baby className="w-3 h-3" /> : ct === 'RELATIONSHIP' ? <Heart className="w-3 h-3" /> : null;

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const errFor = (scope, key) => (touched[errKey(scope, key)] ? errors[errKey(scope, key)] || '' : '');
  const filledOk = (scope, key, value) => {
    const has = !!(value && String(value).trim());
    return has && !errFor(scope, key) && !validateField(scope, key, value);
  };

  return (
    <div className="space-y-6" data-testid="patients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-semibold text-[#1A3330] font-['Outfit']">Patients</h1>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl" data-testid="add-client-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Patient
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#4A6B68]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, diagnosis code, BVIP..." className="pl-10 rounded-xl border-[#E5F2F1] bg-white" data-testid="patient-search-input" />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="h-9 text-xs rounded-xl border border-[#E5F2F1] bg-white px-3 text-[#1A3330]" data-testid="filter-status">
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="ALL">All</option>
        </select>
        <select value={filterBvip} onChange={e => setFilterBvip(e.target.value)} className="h-9 text-xs rounded-xl border border-[#E5F2F1] bg-white px-3 text-[#1A3330]" data-testid="filter-bvip">
          <option value="">All BVIP</option>
          <option value="Mudha">Mudha</option>
          <option value="Kshipta">Kshipta</option>
          <option value="Vikshipta">Vikshipta</option>
          <option value="Ekagra">Ekagra</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="patient-cards">
        {clients.map(c => {
          const inactive = c.is_active === false;
          return (
            <Link to={`/patients/${c._id}`} key={c._id} data-testid={inactive ? `inactive-client-${c._id}` : `active-client-${c._id}`}>
              <Card className={`bg-white rounded-2xl hover:shadow-[0_8px_30px_rgba(13,139,125,0.08)] hover:-translate-y-1 transition-all duration-300 cursor-pointer h-full ${inactive ? 'opacity-60 grayscale border-dashed border-2 border-[#D3544E]/40' : 'border border-[#E5F2F1]'}`}>
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-[#0D8B7D]/10 flex items-center justify-center text-[#0D8B7D] font-semibold text-sm flex-shrink-0">
                      {getInitials(c) || c.first_name?.[0] || '?'}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-[#1A3330] truncate">{formatName(c)}</p>
                      <p className="text-xs text-[#4A6B68]">{c.email}</p>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={`text-[10px] border-0 ${c.is_active !== false ? 'bg-[#2E8B57]/10 text-[#2E8B57]' : 'bg-[#D3544E]/10 text-[#D3544E]'}`}>{c.is_active !== false ? 'Active' : 'Inactive'}</Badge>
                      {c.client_type && c.client_type !== 'ADULT' && <Badge className="bg-[#F5B041]/10 text-[#F5B041] text-[10px] border-0 flex items-center gap-0.5">{typeIcon(c.client_type)}{c.client_type}</Badge>}
                    </div>
                  </div>
                  {(c.medical_diagnosis?.length > 0 || c.bvip_diagnosis?.length > 0) ? (
                    <div className="mb-2"><DiagnosisPills medicalDiagnosis={c.medical_diagnosis} bvipDiagnosis={c.bvip_diagnosis} compact /></div>
                  ) : c.diagnosis_tags?.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {c.diagnosis_tags.slice(0, 4).map(tag => <Badge key={tag} className="bg-[#0D8B7D]/10 text-[#0D8B7D] text-[10px] border-0 font-normal">{tag}</Badge>)}
                    </div>
                  ) : null}
                  {c.provisional_diagnosis && (
                    <p className="text-[11px] text-[#4A6B68] italic mb-2 truncate" data-testid={`provisional-diag-${c._id}`}>
                      <span className="font-medium not-italic text-[#1A3330]">Provisional:</span> {c.provisional_diagnosis}
                    </p>
                  )}
                  {c.formulation && (
                    <p className="text-[11px] text-[#4A6B68] italic mb-2 truncate" data-testid={`formulation-${c._id}`}>
                      <span className="font-medium not-italic text-[#1A3330]">Formulation:</span> {c.formulation}
                    </p>
                  )}
                  <div className="flex items-center gap-3 text-xs text-[#4A6B68]">
                    <span className="flex items-center gap-1"><User className="w-3 h-3" />{c.gender || 'N/A'}</span>
                    <span>{c.phone}</span>
                    {c.linked_partner_id && <span className="flex items-center gap-0.5 text-[#F5B041]"><Heart className="w-3 h-3" />Linked</span>}
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
        {clients.length === 0 && <p className="text-sm text-[#4A6B68] col-span-3">No patients found.</p>}
      </div>

      {/* ===== ADD PATIENT MODAL ===== */}
      <Dialog open={showModal} onOpenChange={v => { if (!v) resetForm(); setShowModal(v); }}>
        <DialogContent className="max-w-2xl bg-white rounded-2xl p-0 gap-0 max-h-[90vh]" data-testid="add-patient-modal">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E5F2F1]">
            <DialogTitle className="font-['Outfit'] text-[#1A3330]">Add New Patient</DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[calc(90vh-80px)]">
            <form ref={formRef} onSubmit={handleCreate} className="p-6 space-y-5" noValidate>
              {error && (
                <p className="text-sm text-[#D3544E] bg-[#D3544E]/10 p-3 rounded-xl" data-testid="add-patient-error">
                  {error}
                </p>
              )}

              {/* Client Type Selector */}
              <div>
                <Label className="text-sm font-medium text-[#1A3330]">Client Type <span className="text-[#D3544E]">*</span></Label>
                <div className="grid grid-cols-3 gap-2 mt-1.5">
                  {[
                    { v: 'ADULT', icon: User, label: 'Adult', desc: 'Individual client' },
                    { v: 'CHILD', icon: Baby, label: 'Child', desc: 'With caregiver' },
                    { v: 'RELATIONSHIP', icon: Heart, label: 'Relationship', desc: 'Two partners' },
                  ].map(ct => (
                    <button key={ct.v} type="button" onClick={() => { setClientType(ct.v); setErrors({}); setTouched({}); }} data-testid={`client-type-${ct.v.toLowerCase()}`}
                      className={`p-3 rounded-xl border-2 text-left transition-all ${clientType === ct.v ? 'border-[#0D8B7D] bg-[#0D8B7D]/5' : 'border-[#E5F2F1] hover:border-[#0D8B7D]/30'}`}>
                      <ct.icon className={`w-5 h-5 mb-1 ${clientType === ct.v ? 'text-[#0D8B7D]' : 'text-[#4A6B68]'}`} />
                      <p className={`text-sm font-medium ${clientType === ct.v ? 'text-[#0D8B7D]' : 'text-[#1A3330]'}`}>{ct.label}</p>
                      <p className="text-[10px] text-[#4A6B68]">{ct.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* ===== ADULT / CHILD FORM ===== */}
              {clientType !== 'RELATIONSHIP' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div data-fieldkey="form.first_name">
                      <FieldShell label="First Name" required testId="patient-first-name-wrap"
                        error={errFor('form','first_name')}
                        filled={filledOk('form','first_name', form.first_name)}>
                        <Input value={form.first_name}
                          onChange={e => onChange('form','first_name', e.target.value, setForm, form)}
                          onBlur={() => onBlur('form','first_name', form.first_name)}
                          className={inputCls(errFor('form','first_name'))} data-testid="patient-first-name" />
                      </FieldShell>
                    </div>
                    <div data-fieldkey="form.last_name">
                      <FieldShell label="Last Name" testId="patient-last-name-wrap"
                        error={errFor('form','last_name')}
                        filled={filledOk('form','last_name', form.last_name)}>
                        <Input value={form.last_name}
                          onChange={e => onChange('form','last_name', e.target.value, setForm, form)}
                          onBlur={() => onBlur('form','last_name', form.last_name)}
                          className={inputCls(errFor('form','last_name'))} data-testid="patient-last-name" />
                      </FieldShell>
                    </div>
                    <div data-fieldkey="form.email">
                      <FieldShell label="Email" required testId="patient-email-wrap"
                        error={errFor('form','email')}
                        filled={filledOk('form','email', form.email)}>
                        <Input type="email" value={form.email}
                          onChange={e => onChange('form','email', e.target.value, setForm, form)}
                          onBlur={() => onBlur('form','email', form.email)}
                          className={inputCls(errFor('form','email'))} data-testid="patient-email" />
                      </FieldShell>
                    </div>
                    <div data-fieldkey="form.password">
                      <FieldShell label="Password" required testId="patient-password-wrap"
                        error={errFor('form','password')}
                        filled={filledOk('form','password', form.password)}>
                        <Input type="password" value={form.password}
                          onChange={e => onChange('form','password', e.target.value, setForm, form)}
                          onBlur={() => onBlur('form','password', form.password)}
                          className={inputCls(errFor('form','password'))} data-testid="patient-password" />
                      </FieldShell>
                      <PasswordStrength value={form.password} testId="patient-password-strength" />
                    </div>
                    <div data-fieldkey="form.phone">
                      <FieldShell label="Phone" required testId="patient-phone-wrap"
                        error={errFor('form','phone')}>
                        <PhoneField value={form.phone}
                          onChange={v => { onChange('form','phone', v, setForm, form); }}
                          inputProps={{ onBlur: () => onBlur('form','phone', form.phone) }}
                          error={!!errFor('form','phone')}
                          testId="patient-phone" />
                      </FieldShell>
                    </div>
                    <div data-fieldkey="form.date_of_birth">
                      <FieldShell label="Date of Birth" required testId="patient-dob-wrap"
                        error={errFor('form','date_of_birth')}
                        filled={filledOk('form','date_of_birth', form.date_of_birth)}>
                        <Input type="date" value={form.date_of_birth}
                          max={new Date().toISOString().slice(0,10)}
                          onChange={e => onChange('form','date_of_birth', e.target.value, setForm, form)}
                          onBlur={() => onBlur('form','date_of_birth', form.date_of_birth)}
                          className={inputCls(errFor('form','date_of_birth'))} data-testid="patient-dob" />
                      </FieldShell>
                    </div>
                    <div data-fieldkey="form.gender">
                      <FieldShell label="Gender" required testId="patient-gender-wrap"
                        error={errFor('form','gender')}
                        filled={filledOk('form','gender', form.gender)}>
                        <Select value={form.gender} onValueChange={v => { onChange('form','gender', v, setForm, form); onBlur('form','gender', v); }}>
                          <SelectTrigger className={`rounded-xl h-9 text-sm ${errFor('form','gender') ? 'border-[#D3544E]' : 'border-[#E5F2F1]'}`} data-testid="patient-gender"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem><SelectItem value="Prefer not to say">Prefer not to say</SelectItem></SelectContent>
                        </Select>
                      </FieldShell>
                    </div>
                    <div data-fieldkey="form.primary_therapist_id">
                      <FieldShell label="Primary Therapist" required testId="patient-therapist-wrap"
                        error={errFor('form','primary_therapist_id')}
                        filled={filledOk('form','primary_therapist_id', form.primary_therapist_id)}>
                        <Select value={form.primary_therapist_id} onValueChange={v => { onChange('form','primary_therapist_id', v, setForm, form); onBlur('form','primary_therapist_id', v); }}>
                          <SelectTrigger className={`rounded-xl h-9 text-sm ${errFor('form','primary_therapist_id') ? 'border-[#D3544E]' : 'border-[#E5F2F1]'}`} data-testid="patient-therapist"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>{therapists.map(t => <SelectItem key={t._id} value={t._id}>{formatName(t)}</SelectItem>)}</SelectContent>
                        </Select>
                      </FieldShell>
                    </div>
                    <div className="col-span-2 border-t border-[#E5F2F1] pt-3 mt-1">
                      <p className="text-xs font-semibold text-[#1A3330] mb-2 uppercase tracking-wider">Diagnosis</p>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <MedicalDiagnosisSelect value={medDiag} onChange={setMedDiag} />
                        <BvipDiagnosisSelect value={bvipDiag} onChange={setBvipDiag} />
                      </div>
                    </div>
                  </div>

                  {/* Caregiver section for CHILD */}
                  {clientType === 'CHILD' && (
                    <div className="border-2 border-[#F5B041]/30 rounded-2xl p-4 bg-[#F5B041]/5" data-testid="caregiver-section">
                      <div className="flex items-center gap-2 mb-3">
                        <Users2 className="w-4 h-4 text-[#F5B041]" />
                        <h4 className="text-sm font-semibold text-[#1A3330] font-['Outfit']">Caregiver Details <span className="text-[#D3544E]">*</span></h4>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div data-fieldkey="caregiver.first_name">
                          <FieldShell label="First Name" required testId="caregiver-first-name-wrap"
                            error={errFor('caregiver','first_name')}
                            filled={filledOk('caregiver','first_name', caregiver.first_name)}>
                            <Input value={caregiver.first_name}
                              onChange={e => onChange('caregiver','first_name', e.target.value, setCaregiver, caregiver)}
                              onBlur={() => onBlur('caregiver','first_name', caregiver.first_name)}
                              className={inputCls(errFor('caregiver','first_name'))} data-testid="caregiver-first-name" />
                          </FieldShell>
                        </div>
                        <div data-fieldkey="caregiver.last_name">
                          <FieldShell label="Last Name" testId="caregiver-last-name-wrap"
                            error={errFor('caregiver','last_name')}
                            filled={filledOk('caregiver','last_name', caregiver.last_name)}>
                            <Input value={caregiver.last_name}
                              onChange={e => onChange('caregiver','last_name', e.target.value, setCaregiver, caregiver)}
                              onBlur={() => onBlur('caregiver','last_name', caregiver.last_name)}
                              className={inputCls(errFor('caregiver','last_name'))} data-testid="caregiver-last-name" />
                          </FieldShell>
                        </div>
                        <div data-fieldkey="caregiver.relationship">
                          <FieldShell label="Relationship" required testId="caregiver-relationship-wrap"
                            error={errFor('caregiver','relationship')}
                            filled={filledOk('caregiver','relationship', caregiver.relationship)}>
                            <Select value={caregiver.relationship} onValueChange={v => { onChange('caregiver','relationship', v, setCaregiver, caregiver); onBlur('caregiver','relationship', v); }}>
                              <SelectTrigger className={`rounded-xl h-9 text-sm ${errFor('caregiver','relationship') ? 'border-[#D3544E]' : 'border-[#E5F2F1]'}`} data-testid="caregiver-relationship"><SelectValue placeholder="Select" /></SelectTrigger>
                              <SelectContent><SelectItem value="Parent">Parent</SelectItem><SelectItem value="Grandparent">Grandparent</SelectItem><SelectItem value="Guardian">Guardian</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
                            </Select>
                          </FieldShell>
                        </div>
                        <div data-fieldkey="caregiver.phone">
                          <FieldShell label="Phone" required testId="caregiver-phone-wrap"
                            error={errFor('caregiver','phone')}>
                            <PhoneField value={caregiver.phone}
                              onChange={v => onChange('caregiver','phone', v, setCaregiver, caregiver)}
                              inputProps={{ onBlur: () => onBlur('caregiver','phone', caregiver.phone) }}
                              error={!!errFor('caregiver','phone')}
                              testId="caregiver-phone" />
                          </FieldShell>
                        </div>
                        <div data-fieldkey="caregiver.email">
                          <FieldShell label="Email" required testId="caregiver-email-wrap"
                            error={errFor('caregiver','email')}
                            filled={filledOk('caregiver','email', caregiver.email)}>
                            <Input type="email" value={caregiver.email}
                              onChange={e => onChange('caregiver','email', e.target.value, setCaregiver, caregiver)}
                              onBlur={() => onBlur('caregiver','email', caregiver.email)}
                              className={inputCls(errFor('caregiver','email'))} data-testid="caregiver-email" />
                          </FieldShell>
                        </div>
                        <div className="col-span-2">
                          <FieldShell label="Address" testId="caregiver-address-wrap">
                            <Input value={caregiver.address}
                              onChange={e => setCaregiver({ ...caregiver, address: e.target.value })}
                              className="rounded-xl border-[#E5F2F1] h-9 text-sm" data-testid="caregiver-address" />
                          </FieldShell>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ===== RELATIONSHIP FORM ===== */}
              {clientType === 'RELATIONSHIP' && (
                <>
                  <div className="border-t border-[#E5F2F1] pt-3 mt-1">
                    <p className="text-xs font-semibold text-[#1A3330] mb-2 uppercase tracking-wider">Shared Diagnosis</p>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      <MedicalDiagnosisSelect value={relMedDiag} onChange={setRelMedDiag} />
                      <BvipDiagnosisSelect value={relBvipDiag} onChange={setRelBvipDiag} />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    {/* Partner 1 */}
                    <PartnerCard
                      scope="p1" tone="teal"
                      label="Partner 1"
                      person={partner1} setPerson={setPartner1}
                      therapists={therapists}
                      errFor={errFor} filledOk={filledOk}
                      onChange={onChange} onBlur={onBlur}
                      trenderers={trenderers}
                    />
                    {/* Partner 2 */}
                    <PartnerCard
                      scope="p2" tone="orange"
                      label="Partner 2"
                      person={partner2} setPerson={setPartner2}
                      therapists={therapists}
                      errFor={errFor} filledOk={filledOk}
                      onChange={onChange} onBlur={onBlur}
                      trenderers={trenderers}
                    />
                  </div>
                </>
              )}

              <Button type="submit" disabled={submitting}
                className="w-full bg-[#0D8B7D] hover:bg-[#0B786C] text-white rounded-xl"
                data-testid="create-patient-btn">
                {submitting ? 'Submitting…' : (clientType === 'RELATIONSHIP' ? 'Create Both Partners' : `Create ${clientType === 'CHILD' ? 'Child ' : ''}Patient`)}
              </Button>
            </form>
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===== Sub-component: a single partner card in Relationship form =====
function PartnerCard({ scope, tone, label, person, setPerson, therapists, errFor, filledOk, onChange, onBlur, trenderers }) {
  const toneCls = tone === 'teal'
    ? { border: 'border-[#0D8B7D]/20', bg: 'bg-[#0D8B7D]/5', text: 'text-[#0D8B7D]' }
    : { border: 'border-[#F5B041]/20', bg: 'bg-[#F5B041]/5', text: 'text-[#F5B041]' };
  return (
    <div className={`border-2 ${toneCls.border} rounded-2xl p-4 ${toneCls.bg}`} data-testid={`${scope === 'p1' ? 'partner1' : 'partner2'}-section`}>
      <h4 className={`text-sm font-semibold ${toneCls.text} font-['Outfit'] mb-3`}>{label}</h4>
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div data-fieldkey={`${scope}.first_name`}>
            <FieldShell label="First Name" required labelClass="text-[10px]"
              error={errFor(scope,'first_name')}
              filled={filledOk(scope,'first_name', person.first_name)}>
              <Input value={person.first_name}
                onChange={e => onChange(scope,'first_name', e.target.value, setPerson, person)}
                onBlur={() => onBlur(scope,'first_name', person.first_name)}
                className={smInputCls(errFor(scope,'first_name'))}
                data-testid={`${scope}-first-name`} />
            </FieldShell>
          </div>
          <div data-fieldkey={`${scope}.last_name`}>
            <FieldShell label="Last Name" labelClass="text-[10px]"
              error={errFor(scope,'last_name')}
              filled={filledOk(scope,'last_name', person.last_name)}>
              <Input value={person.last_name}
                onChange={e => onChange(scope,'last_name', e.target.value, setPerson, person)}
                onBlur={() => onBlur(scope,'last_name', person.last_name)}
                className={smInputCls(errFor(scope,'last_name'))}
                data-testid={`${scope}-last-name`} />
            </FieldShell>
          </div>
        </div>
        <div data-fieldkey={`${scope}.email`}>
          <FieldShell label="Email" required labelClass="text-[10px]"
            error={errFor(scope,'email')}
            filled={filledOk(scope,'email', person.email)}>
            <Input type="email" value={person.email}
              onChange={e => onChange(scope,'email', e.target.value, setPerson, person)}
              onBlur={() => onBlur(scope,'email', person.email)}
              className={smInputCls(errFor(scope,'email'))}
              data-testid={`${scope}-email`} />
          </FieldShell>
        </div>
        <div data-fieldkey={`${scope}.password`}>
          <FieldShell label="Password" required labelClass="text-[10px]"
            error={errFor(scope,'password')}
            filled={filledOk(scope,'password', person.password)}>
            <Input type="password" value={person.password}
              onChange={e => onChange(scope,'password', e.target.value, setPerson, person)}
              onBlur={() => onBlur(scope,'password', person.password)}
              className={smInputCls(errFor(scope,'password'))}
              data-testid={`${scope}-password`} />
          </FieldShell>
          <PasswordStrength value={person.password} testId={`${scope}-password-strength`} />
        </div>
        <div className="grid grid-cols-1 gap-2">
          <div data-fieldkey={`${scope}.phone`}>
            <FieldShell label="Phone" required labelClass="text-[10px]"
              error={errFor(scope,'phone')}>
              <PhoneField value={person.phone}
                onChange={v => onChange(scope,'phone', v, setPerson, person)}
                inputProps={{ onBlur: () => onBlur(scope,'phone', person.phone) }}
                error={!!errFor(scope,'phone')}
                testId={`${scope}-phone`} />
            </FieldShell>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div data-fieldkey={`${scope}.date_of_birth`}>
            <FieldShell label="DOB" required labelClass="text-[10px]"
              error={errFor(scope,'date_of_birth')}
              filled={filledOk(scope,'date_of_birth', person.date_of_birth)}>
              <Input type="date" value={person.date_of_birth}
                max={new Date().toISOString().slice(0,10)}
                onChange={e => onChange(scope,'date_of_birth', e.target.value, setPerson, person)}
                onBlur={() => onBlur(scope,'date_of_birth', person.date_of_birth)}
                className={smInputCls(errFor(scope,'date_of_birth'))}
                data-testid={`${scope}-dob`} />
            </FieldShell>
          </div>
          <div data-fieldkey={`${scope}.gender`}>
            <FieldShell label="Gender" required labelClass="text-[10px]"
              error={errFor(scope,'gender')}
              filled={filledOk(scope,'gender', person.gender)}>
              <Select value={person.gender} onValueChange={v => { onChange(scope,'gender', v, setPerson, person); onBlur(scope,'gender', v); }}>
                <SelectTrigger className={`rounded-xl h-8 text-xs ${errFor(scope,'gender') ? 'border-[#D3544E]' : 'border-[#E5F2F1]'}`} data-testid={`${scope}-gender`}><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent><SelectItem value="Male">Male</SelectItem><SelectItem value="Female">Female</SelectItem><SelectItem value="Other">Other</SelectItem></SelectContent>
              </Select>
            </FieldShell>
          </div>
        </div>
        <div>
          <FieldShell label="Blood Group" labelClass="text-[10px]">
            <Input value={person.blood_group}
              onChange={e => setPerson({ ...person, blood_group: e.target.value })}
              className="rounded-xl border-[#E5F2F1] h-8 text-xs" placeholder="O+" data-testid={`${scope}-blood-group`} />
          </FieldShell>
        </div>
        <div data-fieldkey={`${scope}.primary_therapist_id`}>
          <FieldShell label="Primary Therapist" required labelClass="text-[10px]"
            error={errFor(scope,'primary_therapist_id')}>
            <SearchableSelect
              value={person.primary_therapist_id}
              onChange={(id) => { onChange(scope,'primary_therapist_id', id, setPerson, person); onBlur(scope,'primary_therapist_id', id); }}
              options={therapists}
              placeholder="Search therapist…"
              testId={`${scope}-therapist`}
              {...trenderers}
            />
          </FieldShell>
        </div>
      </div>
    </div>
  );
}
